import {
  CreatePaymentIntentInputModel,
  PaymentSheet,
  PaymentAccountInputModel,
  OnboardingResponseModel,
  AccountSessionResponseModel,
  PaymentBookingsResponseModel,
} from '../types/PaymentModels';
import { api, getApiBaseUrl, getApiKey } from './api';
import axios from 'axios';

const API_BASE_URL = getApiBaseUrl();
const API_KEY = getApiKey();

// NOTE: Using shared api instance from './api'
// Special loading behavior for payment endpoints handled below

const professionalPaymentsUrlPrefix =
  '/v1/payment/professional-payment-bookings';
const userPaymentsUrlPrefix = '/v1/payment/user-payment-bookings';
const urlToSkipGlobalLoading = [
  professionalPaymentsUrlPrefix,
  userPaymentsUrlPrefix,
];

// Add specialized loading interceptors for payment endpoints
api.interceptors.request.use((config) => {
  const shouldSkipLoading = urlToSkipGlobalLoading.some((url) =>
    config.url?.includes(url)
  );

  if (!shouldSkipLoading) {
    const event = new CustomEvent('api-loading', { detail: true });
    window.dispatchEvent(event);
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const shouldSkipLoading = urlToSkipGlobalLoading.some((url) =>
      response.config.url?.includes(url)
    );

    if (!shouldSkipLoading) {
      const event = new CustomEvent('api-loading', { detail: false });
      window.dispatchEvent(event);
    }

    return response;
  },
  (error) => {
    const shouldSkipLoading = urlToSkipGlobalLoading.some((url) =>
      error.config?.url?.includes(url)
    );

    if (!shouldSkipLoading) {
      const event = new CustomEvent('api-loading', { detail: false });
      window.dispatchEvent(event);
    }

    return Promise.reject(error);
  }
);

// 401 handling and other error logging handled by shared api instance from './api'

export const createPaymentAccount = async (
  token: string
): Promise<PaymentAccountInputModel> => {
  try {
    const response = await api.post<PaymentAccountInputModel>(
      '/v1/payment/create-account',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Network Error creating payment intent:', error);
      return Promise.reject(error);
    } else {
      console.error('Error creating payment intent:', error);
      return Promise.reject(error);
    }
  }
};

export const getOnboardingStatus = async (
  token: string
): Promise<OnboardingResponseModel> => {
  try {
    const response = await api.get<OnboardingResponseModel>(
      '/v1/payment/onboarding-status',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching payment status:', error);
    return Promise.reject(error);
  }
};

export const getAccountSession = async (
  token: string
): Promise<AccountSessionResponseModel> => {
  try {
    const response = await api.get<AccountSessionResponseModel>(
      '/v1/payment/account-session',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching payment status:', error);
    return Promise.reject(error);
  }
};

export const getPublishableKey = async (token: string): Promise<string> => {
  try {
    const response = await api.get<string>('/v1/payment/publishable-key', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching publishable key:', error);
    return Promise.reject(error);
  }
};

export const createPaymentIntent = async (
  model: CreatePaymentIntentInputModel,
  token: string
): Promise<PaymentSheet> => {
  try {
    const response = await api.post<PaymentSheet>('/v1/payment/intent', model, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Network Error creating payment intent:', error);
      return Promise.reject(error);
    } else {
      console.error('Error creating payment intent:', error);
      return Promise.reject(error);
    }
  }
};

export const getPaymentStatusByPaymentIntent = async (
  paymentIntent: string,
  token: string
): Promise<void> => {
  try {
    const response = await api.get<string>(
      `/v1/payment/payment-status-payment-intent/${paymentIntent}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error) {
    console.error('Error fetching payment status:', error);
  }
};

export const getProfessionalPaymentBookingsByMonth = async (
  month: string,
  token: string
): Promise<PaymentBookingsResponseModel> => {
  try {
    const response = await api.get<PaymentBookingsResponseModel>(
      `${professionalPaymentsUrlPrefix}?month=${month}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching professional payment bookings:', error);
    return Promise.reject(error);
  }
};

export const getUserPaymentBookingsByMonth = async (
  month: string,
  token: string
): Promise<PaymentBookingsResponseModel> => {
  try {
    const response = await api.get<PaymentBookingsResponseModel>(
      `${userPaymentsUrlPrefix}?month=${month}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching user payment bookings:', error);
    return Promise.reject(error);
  }
};
